import React from 'react'

import { faBookOpen } from '@fortawesome/pro-light-svg-icons/faBookOpen'
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck'
import { faGift } from '@fortawesome/pro-light-svg-icons/faGift'
import { faInfinity } from '@fortawesome/pro-light-svg-icons/faInfinity'
import { Container, Skeleton, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { type TierId, useTierOptions } from '@/shared/cart/tierOptions'
import { pxToRem } from '@/shared/styles/themes'

const TIER_ICONS: Record<TierId, typeof faGift> = {
  single_story: faGift,
  unlimited: faInfinity,
  unlimited_book: faBookOpen,
}

// Creator-tilted subtitles. Lean into per-project economics and showcase
// value rather than the gift framing on the consumer homepage.
const TIER_SUBTITLE: Record<TierId, string> = {
  single_story: 'Test the workflow on one project before scaling.',
  unlimited: 'The standard for freelancers — unlimited projects, no per-book fees.',
  unlimited_book: 'Includes a sample hardcover for client demos and your portfolio.',
}

export const CreatorsPackages: React.FC = () => {
  // Prices come from the backend catalog (USD), so they load client-side.
  const { tiers } = useTierOptions()

  return (
    <StyledSection id="pricing">
      <Container maxWidth="lg">
        <StyledHeader>
          <StyledEyebrow>One Price. Every Project.</StyledEyebrow>
          <StyledHeadline variant="h2">
            Tools that pay for themselves on book #1
          </StyledHeadline>
          <StyledSubhead variant="body1">
            One-time payment. No per-project licensing, no monthly seat fees,
            no surprise renewals. Charge clients $200-500 per book — your
            tools cost less than a single delivery.
          </StyledSubhead>
        </StyledHeader>

        <StyledGrid>
          {tiers.map((tier) => {
            const isPopular = tier.badge === 'Popular'

            return (
              <StyledCard key={tier.id} $highlighted={isPopular}>
                {tier.badge && (
                  <StyledBadge $popular={isPopular}>{tier.badge}</StyledBadge>
                )}
                <StyledIconBadge $highlighted={isPopular}>
                  <Icon icon={TIER_ICONS[tier.id] ?? faGift} sx={{ fontSize: pxToRem(22) }} />
                </StyledIconBadge>
                <StyledTierName variant="h5">{tier.name}</StyledTierName>
                <StyledTierSub variant="body2">{TIER_SUBTITLE[tier.id] ?? ''}</StyledTierSub>

                <StyledPriceRow>
                  {tier.stickerPrice && (
                    <StyledStrikethrough>{tier.stickerPrice}</StyledStrikethrough>
                  )}
                  <StyledPrice>{tier.price || <Skeleton variant="text" width={80} sx={{ display: 'inline-block' }} />}</StyledPrice>
                  {tier.savingsPercent && (
                    <StyledSavings>{tier.savingsPercent}</StyledSavings>
                  )}
                </StyledPriceRow>
                <StyledOneTime>one-time payment</StyledOneTime>

                <StyledFeatureList>
                  {tier.features.map((feature) => (
                    <StyledFeature key={feature}>
                      <Icon icon={faCheck} sx={{ fontSize: pxToRem(14) }} />
                      <span>{feature}</span>
                    </StyledFeature>
                  ))}
                </StyledFeatureList>

                <StyledCta
                  arrow={!isPopular}
                  primary={isPopular}
                  outline={!isPopular}
                  size="large"
                  href="/shop"
                  $highlighted={isPopular}
                >
                  {isPopular ? 'Get Unlimited' : 'Choose This Package'}
                </StyledCta>
              </StyledCard>
            )
          })}
        </StyledGrid>

        <StyledFootnote variant="body2">
          Every package unlocks the full Canva-style editor. Drag-and-drop layouts, character consistency, print-ready 300 DPI exports — yours forever.
        </StyledFootnote>
      </Container>
    </StyledSection>
  )
}

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(180deg, #FFF5EE 0%, #FFFAF6 100%)',
  padding: theme.spacing(6, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(10, 0),
  },
}))

const StyledHeader = styled('div')(({ theme }) => ({
  textAlign: 'center',
  maxWidth: pxToRem(640),
  margin: '0 auto',
  marginBottom: theme.spacing(5),

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(7),
  },
}))

const StyledEyebrow = styled('div')(({ theme }) => ({
  display: 'inline-block',
  color: theme.palette.primary.main,
  backgroundColor: 'rgba(220, 174, 150, 0.18)',
  fontSize: pxToRem(12),
  fontWeight: 700,
  letterSpacing: pxToRem(1.5),
  textTransform: 'uppercase',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: pxToRem(999),
  marginBottom: theme.spacing(2),
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  lineHeight: 1.2,
}))

const StyledSubhead = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.8,
  fontSize: pxToRem(16),
  lineHeight: 1.55,
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  alignItems: 'stretch',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
  },
}))

const StyledCard = styled('div', { shouldForwardProp: (prop) => prop !== '$highlighted' })<{ $highlighted?: boolean }>(({ theme, $highlighted }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  borderRadius: pxToRem(20),
  padding: theme.spacing(4, 3),
  border: $highlighted
    ? `2px solid ${theme.palette.primary.main}`
    : '1px solid rgba(108, 60, 20, 0.12)',
  boxShadow: $highlighted
    ? '0 20px 50px rgba(108, 60, 20, 0.18)'
    : '0 6px 20px rgba(108, 60, 20, 0.06)',

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5, 3.5),
    transform: $highlighted ? 'translateY(-12px)' : 'none',
  },
}))

const StyledBadge = styled('span', { shouldForwardProp: (prop) => prop !== '$popular' })<{ $popular?: boolean }>(({ theme, $popular }) => ({
  position: 'absolute',
  top: pxToRem(-12),
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: $popular ? theme.palette.primary.main : theme.palette.secondary.main,
  color: '#FFFFFF',
  fontSize: pxToRem(11),
  fontWeight: 700,
  letterSpacing: pxToRem(1),
  textTransform: 'uppercase',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: pxToRem(999),
  whiteSpace: 'nowrap',
}))

const StyledIconBadge = styled('div', { shouldForwardProp: (prop) => prop !== '$highlighted' })<{ $highlighted?: boolean }>(({ theme, $highlighted }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(48),
  height: pxToRem(48),
  borderRadius: '50%',
  backgroundColor: $highlighted ? theme.palette.primary.main : 'rgba(220, 174, 150, 0.2)',
  color: $highlighted ? '#FFFFFF' : theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}))

const StyledTierName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}))

const StyledTierSub = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2.5),
  minHeight: pxToRem(42),
  lineHeight: 1.5,
}))

const StyledPriceRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}))

const StyledStrikethrough = styled('span')(({ theme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.text.disabled,
  fontSize: pxToRem(16),
}))

const StyledPrice = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(34),
  fontWeight: 700,
  lineHeight: 1,
}))

const StyledSavings = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: '#FFFFFF',
  fontSize: pxToRem(11),
  fontWeight: 700,
  letterSpacing: pxToRem(0.5),
  padding: theme.spacing(0.25, 1),
  borderRadius: pxToRem(999),
}))

const StyledOneTime = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: pxToRem(12),
  textTransform: 'uppercase',
  letterSpacing: pxToRem(1),
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2.5),
}))

const StyledFeatureList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  marginBottom: theme.spacing(3),
  flexGrow: 1,
}))

const StyledFeature = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  color: theme.palette.primary.main,
  fontSize: pxToRem(14),
  lineHeight: 1.5,

  '& svg': {
    marginTop: pxToRem(3),
    color: theme.palette.success.main,
    flexShrink: 0,
  },
}))

const StyledCta = styled(Button, { shouldForwardProp: (prop) => prop !== '$highlighted' })<{ $highlighted?: boolean }>(({ theme, $highlighted }) => ({
  fontWeight: 600,
  padding: theme.spacing(1.75, 3),
  marginTop: 'auto',
  ...($highlighted ? {
    color: theme.palette.secondary.main,
  } : {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(255,255,255,0.5)',
    },
  }),
}))

const StyledFootnote = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(4),
  fontSize: pxToRem(13),
}))
